/**
 * Guard for /exams/:examSlug routes. Ensures:
 * 1. User is authenticated (must be used inside protected layout).
 * 2. :examSlug is valid.
 * 3. User's profile exam selection matches :examSlug (or user is admin).
 * If not allowed, redirects to dashboard so users cannot access other exams by typing URLs.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Outlet, useParams, useNavigate, Navigate, useOutletContext } from 'react-router-dom';
import { auth } from '../../firebase';
import userService from '../../services/enhancedUserService';
import examService from '../../services/examService';
import adminService from '../../services/adminService';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import {
  parseExamSlug,
  isValidExamSlug,
  getAllowedExamSlug,
  getExamSlug,
} from '../../utils/examSlug';

const ExamSlugContext = createContext(null);

export function useExamFromSlug() {
  const ctx = useContext(ExamSlugContext);
  return ctx;
}

export default function ExamSlugRoute({ selectedExamData: preloadedExamData }) {
  const { lang, examSlug } = useParams();
  const navigate = useNavigate();
  // If App.jsx already loaded the exam and it matches the URL slug, skip the async load
  // entirely so the page renders without any loading flash.
  const [state, setState] = useState(() => {
    const slug = (examSlug || '').toLowerCase().trim();
    if (
      preloadedExamData?.examProvider &&
      preloadedExamData?.level &&
      preloadedExamData?.examProvider !== undefined &&
      getExamSlug(preloadedExamData.examProvider, preloadedExamData.level) === slug
    ) {
      return { status: 'ok', examData: preloadedExamData, allowedSlug: slug };
    }
    return { status: 'loading', examData: null, allowedSlug: '' };
  });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        if (!cancelled) setState({ status: 'no-auth', examData: null, allowedSlug: '' });
        return;
      }

      const slug = (examSlug || '').toLowerCase().trim();
      if (!isValidExamSlug(slug)) {
        if (!cancelled) setState({ status: 'invalid-slug', examData: null, allowedSlug: '' });
        return;
      }

      const selection = await userService.getExamSelection(uid);
      const allowedSlug = getAllowedExamSlug(selection);
      const isAdmin = adminService.isAdmin();

      if (!allowedSlug) {
        if (!cancelled) setState({ status: 'no-selection', examData: null, allowedSlug: '' });
        return;
      }

      if (allowedSlug !== slug && !isAdmin) {
        if (!cancelled) setState({ status: 'forbidden', examData: null, allowedSlug });
        return;
      }

      const parsed = parseExamSlug(slug);
      if (!parsed) {
        if (!cancelled) setState({ status: 'invalid-slug', examData: null, allowedSlug: '' });
        return;
      }

      try {
        const examData = await examService.loadExam(parsed.provider, parsed.level);
        if (!cancelled) setState({ status: 'ok', examData, allowedSlug });
      } catch (err) {
        console.error('ExamSlugRoute: failed to load exam', err);
        if (!cancelled) setState({ status: 'error', examData: null, allowedSlug: '' });
      }
    };

    run();
    return () => { cancelled = true; };
  }, [examSlug]);

  if (state.status === 'no-auth') {
    return <Navigate to={`/${lang || 'en'}/auth`} replace />;
  }

  if (state.status === 'invalid-slug') {
    return <Navigate to={`/${lang || 'en'}/dashboard`} replace />;
  }

  if (state.status === 'no-selection') {
    return <Navigate to={`/${lang || 'en'}/study-plan/wizard`} replace />;
  }

  if (state.status === 'forbidden') {
    const to = `/${lang || 'en'}/exams/${state.allowedSlug}`;
    return <Navigate to={to} replace state={{ message: 'exam-access-only-yours' }} />;
  }

  if (state.status === 'error') {
    return <Navigate to={`/${lang || 'en'}/dashboard`} replace />;
  }

  // Move useOutletContext to before any conditional returns to respect Rules of Hooks.
  // (It is called unconditionally; the early-return guards above only affect non-ok states.)
  // Note: all the early returns above (no-auth / invalid-slug / forbidden / error) are reached
  // BEFORE this point, so this hook is effectively always called in the same hook-call order
  // for the 'ok' and 'loading' branches.
  const layoutContext = useOutletContext() || {};

  if (state.status === 'loading' || !state.examData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-gray-500">
        <LogamiLogoSpinner size="lg" decorative={false} alt="Logami loading" />
      </div>
    );
  }
  return (
    <ExamSlugContext.Provider
      value={{
        examData: state.examData,
        examSlug: (examSlug || '').toLowerCase().trim(),
        provider: state.examData.examProvider,
        level: state.examData.level,
      }}
    >
      <Outlet context={{ ...layoutContext, user: auth.currentUser ?? null }} />
    </ExamSlugContext.Provider>
  );
}
